import { DB_TOKEN_CONTRACTS, FASTNEAR_API_URL, NEAR_RPC_URL, NFT_CONTRACTS, apiUrl } from "../config";

const FALLBACK_NFT_CONTRACT_ID = "siberiashop.mintbase1.near";
const DEFAULT_PAGE_SIZE = 50;
const OWNER_ACCOUNT_RE = /\b[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?\b/g;

function encodeArgs(args = {}) {
  const json = JSON.stringify(args);
  if (typeof window !== "undefined" && typeof window.btoa === "function") {
    return window.btoa(unescape(encodeURIComponent(json)));
  }
  return Buffer.from(json, "utf8").toString("base64");
}

function encodeTextBase64(value = "") {
  const text = String(value);
  if (typeof window !== "undefined" && typeof window.btoa === "function") {
    return window.btoa(unescape(encodeURIComponent(text)));
  }
  return Buffer.from(text, "utf8").toString("base64");
}

function parseRetryAfterMs(value) {
  const raw = String(value || "").trim();
  if (!raw) return 0;

  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.round(seconds * 1000);
  }

  const timestamp = Date.parse(raw);
  if (Number.isFinite(timestamp)) {
    return Math.max(0, timestamp - Date.now());
  }

  return 0;
}

async function rpcRequest(body, { signal } = {}) {
  const response = await fetch(NEAR_RPC_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: "dontcare",
      ...body,
    }),
    signal,
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  const message =
    payload?.error?.message ||
    payload?.message ||
    (!response.ok ? `NEAR RPC error (${response.status})` : "");

  if (payload?.error || !response.ok) {
    const error = new Error(message || "NEAR RPC error");
    error.rpcStatus = response.status || 0;
    error.rpcPayload = payload;
    error.rpcRetryAfterMs = parseRetryAfterMs(response.headers.get("Retry-After"));
    error.rpcRateLimited =
      response.status === 429 ||
      /rate limits? exceeded|too many requests/i.test(String(message || ""));
    throw error;
  }
  return payload.result;
}

function decodeBytesToUtf8(value) {
  const bytes = Array.isArray(value)
    ? Uint8Array.from(value)
    : value instanceof Uint8Array
      ? value
      : null;

  if (!bytes) return "";

  try {
    return new TextDecoder("utf-8").decode(bytes);
  } catch {
    return "";
  }
}

function decodeBase64ToBytes(value) {
  const encoded = String(value || "").trim();
  if (!encoded) return new Uint8Array();

  try {
    if (typeof window !== "undefined" && typeof window.atob === "function") {
      const binary = window.atob(encoded);
      return Uint8Array.from(binary, (char) => char.charCodeAt(0));
    }
  } catch {
    // fall through to Buffer
  }

  try {
    return Uint8Array.from(Buffer.from(encoded, "base64"));
  } catch {
    return new Uint8Array();
  }
}

function decodeRpcStateBytes(value) {
  if (Array.isArray(value)) {
    return Uint8Array.from(value);
  }
  if (value instanceof Uint8Array) {
    return value;
  }
  if (typeof value === "string") {
    return decodeBase64ToBytes(value);
  }
  return new Uint8Array();
}

function shouldPreserveIntegerAsString(token) {
  if (!/^-?\d+$/.test(token)) return false;

  try {
    const value = BigInt(token);
    const min = BigInt(Number.MIN_SAFE_INTEGER);
    const max = BigInt(Number.MAX_SAFE_INTEGER);
    return value < min || value > max;
  } catch {
    return false;
  }
}

export function parseJsonPreservingLargeIntegers(jsonText) {
  const source = String(jsonText ?? "");
  if (!source.trim()) return null;

  let inString = false;
  let escaped = false;
  let transformed = "";

  for (let i = 0; i < source.length; i += 1) {
    const char = source[i];

    if (inString) {
      transformed += char;

      if (escaped) {
        escaped = false;
      } else if (char === "\\") {
        escaped = true;
      } else if (char === "\"") {
        inString = false;
      }

      continue;
    }

    if (char === "\"") {
      inString = true;
      transformed += char;
      continue;
    }

    const startsNumber = char === "-" || (char >= "0" && char <= "9");
    if (!startsNumber) {
      transformed += char;
      continue;
    }

    const prev = i > 0 ? source[i - 1] : "";
    const canStartNumber =
      i === 0 ||
      prev === ":" ||
      prev === "[" ||
      prev === "," ||
      /\s/.test(prev);

    if (!canStartNumber) {
      transformed += char;
      continue;
    }

    let end = i + 1;
    while (end < source.length && /[0-9eE+.-]/.test(source[end])) {
      end += 1;
    }

    const token = source.slice(i, end);
    const isInteger = /^-?\d+$/.test(token);
    transformed += isInteger && shouldPreserveIntegerAsString(token) ? `"${token}"` : token;
    i = end - 1;
  }

  return JSON.parse(transformed);
}

function decodeContractOwnerIdFromStateBytesRaw(value) {
  const bytes = value instanceof Uint8Array ? value : decodeBase64ToBytes(value);
  if (!bytes.length) return "";

  if (bytes.length >= 4) {
    try {
      const length = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(0, true);
      if (length > 0 && 4 + length <= bytes.length) {
        const ownerText = decodeBytesToUtf8(bytes.slice(4, 4 + length)).trim().toLowerCase();
        if (isNearAccountId(ownerText)) return ownerText;
      }
    } catch {
      // fall back to text scan below
    }
  }

  return findOwnerAccountIdInText(decodeBytesToUtf8(bytes));
}

export function decodeContractOwnerIdFromStateBytes(value) {
  return decodeContractOwnerIdFromStateBytesRaw(value);
}

async function rpcViewCall(accountId, methodName, args = {}, options = {}) {
  const result = await rpcRequest(
    {
      method: "query",
      params: {
        request_type: "call_function",
        finality: "final",
        account_id: accountId,
        method_name: methodName,
        args_base64: encodeArgs(args),
      },
    },
    options
  );

  const rawBytes = Array.isArray(result?.result) ? result.result : [];
  const decoded = decodeBytesToUtf8(rawBytes);
  if (!decoded) return null;
  return parseJsonPreservingLargeIntegers(decoded);
}

async function rpcViewState(accountId, prefix = "", options = {}) {
  return rpcRequest(
    {
      method: "query",
      params: {
        request_type: "view_state",
        finality: "final",
        account_id: accountId,
        prefix_base64: encodeTextBase64(prefix),
      },
    },
    options
  );
}

async function rpcViewCode(accountId, options = {}) {
  return rpcRequest(
    {
      method: "query",
      params: {
        request_type: "view_code",
        finality: "final",
        account_id: accountId,
      },
    },
    options
  );
}

function withContractId(item, contractId) {
  if (!item || typeof item !== "object") return item;
  return {
    ...item,
    contractId,
    contract_id: item.contract_id || contractId,
  };
}

function getDiscoveryCandidates() {
  const known = [];
  const seen = new Set();
  const push = (contractId, name = "") => {
    const normalized = String(contractId || "").trim().toLowerCase();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    known.push({ contractId: normalized, name: name || contractId });
  };

  Object.entries(NFT_CONTRACTS || {}).forEach(([name, contractId]) => {
    push(contractId, name);
  });

  (DB_TOKEN_CONTRACTS || [])
    .filter((item) => item?.standard === "nft")
    .forEach((item) => {
      push(item.contractId, item.symbol || item.contractId);
    });

  push(FALLBACK_NFT_CONTRACT_ID, "NUVELOR");
  return known;
}

function getKnownNftContractName(contractId) {
  const normalized = String(contractId || "").trim().toLowerCase();
  if (!normalized) return "";

  for (const [name, value] of Object.entries(NFT_CONTRACTS || {})) {
    if (String(value || "").trim().toLowerCase() === normalized) {
      return name || normalized;
    }
  }

  const dbMatch = (DB_TOKEN_CONTRACTS || []).find(
    (item) =>
      item?.standard === "nft" &&
      String(item?.contractId || "").trim().toLowerCase() === normalized
  );
  if (dbMatch?.symbol) return dbMatch.symbol;

  return "";
}

function toDiscoveredNftContract(entry) {
  const contractId =
    typeof entry === "string"
      ? entry
      : entry?.contract_id || entry?.contractId || entry?.nft_contract_id || "";
  const normalizedContractId = String(contractId || "").trim().toLowerCase();
  if (!normalizedContractId) return null;

  const name =
    (typeof entry === "object" && String(entry?.name || entry?.title || "").trim()) ||
    getKnownNftContractName(normalizedContractId) ||
    normalizedContractId;
  const lastUpdateBlockHeight = Number(
    entry?.last_update_block_height ??
      entry?.lastUpdateBlockHeight ??
      entry?.block_height ??
      0
  );

  return {
    contractId: normalizedContractId,
    name,
    lastUpdateBlockHeight:
      Number.isFinite(lastUpdateBlockHeight) && lastUpdateBlockHeight > 0
        ? Math.floor(lastUpdateBlockHeight)
        : 0,
  };
}

export function normalizeFastNearNftContracts(payload) {
  const source = Array.isArray(payload?.tokens)
    ? payload.tokens
    : Array.isArray(payload?.nfts)
      ? payload.nfts
      : Array.isArray(payload?.contracts)
        ? payload.contracts
        : Array.isArray(payload)
          ? payload
          : [];

  const deduped = new Map();
  source.forEach((entry) => {
    const normalized = toDiscoveredNftContract(entry);
    if (!normalized) return;

    const previous = deduped.get(normalized.contractId);
    if (!previous) {
      deduped.set(normalized.contractId, normalized);
      return;
    }

    deduped.set(normalized.contractId, {
      contractId: normalized.contractId,
      name:
        previous.name && previous.name !== previous.contractId
          ? previous.name
          : normalized.name,
      lastUpdateBlockHeight: Math.max(
        Number(previous.lastUpdateBlockHeight || 0),
        Number(normalized.lastUpdateBlockHeight || 0)
      ),
    });
  });

  return Array.from(deduped.values()).sort((left, right) => {
    const byHeight =
      Number(right.lastUpdateBlockHeight || 0) - Number(left.lastUpdateBlockHeight || 0);
    if (byHeight !== 0) return byHeight;
    return left.contractId.localeCompare(right.contractId);
  });
}

function mergeDiscoveredNftContracts(...lists) {
  const merged = new Map();

  lists.flat().forEach((entry) => {
    const normalized = toDiscoveredNftContract(entry);
    if (!normalized) return;

    const previous = merged.get(normalized.contractId);
    if (!previous) {
      merged.set(normalized.contractId, normalized);
      return;
    }

    merged.set(normalized.contractId, {
      contractId: normalized.contractId,
      name:
        previous.name && previous.name !== previous.contractId
          ? previous.name
          : normalized.name,
      lastUpdateBlockHeight: Math.max(
        Number(previous.lastUpdateBlockHeight || 0),
        Number(normalized.lastUpdateBlockHeight || 0)
      ),
    });
  });

  return Array.from(merged.values());
}

async function fetchFastNearAccountNftContracts(accountId, options = {}) {
  const normalizedAccountId = String(accountId || "").trim().toLowerCase();
  if (!normalizedAccountId) return [];

  const baseUrl = String(FASTNEAR_API_URL || "").replace(/\/+$/, "");
  if (!baseUrl) return [];

  const endpoints = [
    `${baseUrl}/v1/account/${encodeURIComponent(normalizedAccountId)}/nft`,
    `${baseUrl}/v1/account/${encodeURIComponent(normalizedAccountId)}/full`,
    `${baseUrl}/v0/account/${encodeURIComponent(normalizedAccountId)}/nft`,
  ];

  let lastError = null;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        signal: options?.signal,
      });

      if (!response.ok) {
        throw new Error(`FastNEAR API error ${response.status} for ${url}`);
      }

      const payload = await response.json();
      const list = normalizeFastNearNftContracts(payload);
      if (list.length > 0) return list;
    } catch (error) {
      if (error?.name === "AbortError") throw error;
      lastError = error;
    }
  }

  if (lastError) {
    console.debug("fetchFastNearAccountNftContracts fallback to RPC scan:", lastError);
  }
  return [];
}

function normalizeRarityBucketValue(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (["common", "uncommon", "rare", "epic", "legendary", "unique"].includes(raw)) return raw;
  if (raw.includes("legendary")) return "legendary";
  if (raw.includes("epic")) return "epic";
  if (raw.includes("rare")) return "rare";
  if (raw.includes("uncommon")) return "uncommon";
  if (raw.includes("common")) return "common";
  if (raw.includes("unique")) return "unique";
  return "";
}

function parseMetadataExtra(extra) {
  if (!extra) return null;
  if (typeof extra === "object") return extra;
  if (typeof extra !== "string") return null;
  try {
    const parsed = JSON.parse(extra);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function deriveRarityBucketFromMetadata(metadata) {
  const direct =
    normalizeRarityBucketValue(metadata?.rarity_bucket) ||
    normalizeRarityBucketValue(metadata?.rarity);
  if (direct) return direct;

  const extra = parseMetadataExtra(metadata?.extra);
  const extraRarity =
    normalizeRarityBucketValue(extra?.rarity_bucket) ||
    normalizeRarityBucketValue(extra?.rarity);
  if (extraRarity) return extraRarity;

  const title = String(metadata?.title || metadata?.name || "").toLowerCase();
  const description = String(metadata?.description || "").toLowerCase();
  const haystack = `${title} ${description}`;

  const match = title.match(/\(([^)]+)\)/);
  const raw = normalizeRarityBucketValue(match?.[1]);
  if (raw) return raw;

  for (const bucket of ["legendary", "epic", "rare", "uncommon", "common", "unique"]) {
    if (haystack.includes(bucket)) return bucket;
  }

  return "";
}

function extractOwnerId(value) {
  if (typeof value === "string") {
    const candidate = value.trim();
    return candidate || "";
  }

  if (value && typeof value === "object") {
    const nested =
      value.owner_id ??
      value.ownerId ??
      value.owner ??
      value.account_id ??
      value.accountId ??
      "";

    if (typeof nested === "string" && nested.trim()) {
      return nested.trim();
    }
  }

  return "";
}

function isNearAccountId(value) {
  const candidate = String(value || "").trim().toLowerCase();
  if (!candidate || candidate.length < 2 || candidate.length > 64) return false;
  if (!/[.]/.test(candidate)) return false;
  return /^[a-z0-9]+(?:[._-][a-z0-9]+)*(?:\.[a-z0-9]+(?:[._-][a-z0-9]+)*)+$/.test(candidate);
}

function findOwnerAccountIdInText(text) {
  const source = String(text || "");
  if (!source) return "";

  const labeled =
    source.match(/owner(?:_id|Id)?[^a-z0-9]{0,24}([a-z0-9][a-z0-9._-]{1,62}(?:\.[a-z0-9][a-z0-9._-]{0,62})+)/i) ||
    source.match(/([a-z0-9][a-z0-9._-]{1,62}(?:\.[a-z0-9][a-z0-9._-]{0,62})+)[^a-z0-9]{0,24}owner(?:_id|Id)?/i);

  const direct = labeled?.[1] || labeled?.[0] || "";
  if (isNearAccountId(direct)) return direct.toLowerCase();

  const matches = source.match(OWNER_ACCOUNT_RE) || [];
  const accounts = matches.filter(isNearAccountId);
  return accounts[0] ? accounts[0].toLowerCase() : "";
}

export async function callContractView(contractId, methodName, args = {}, options = {}) {
  return rpcViewCall(
    String(contractId || "").trim(),
    String(methodName || "").trim(),
    args ?? {},
    options
  );
}

export async function fetchContractOwnerIdFromState(contractId, options = {}) {
  const normalizedContractId = String(contractId || "").trim();
  if (!normalizedContractId) return "";

  try {
    const state = await rpcViewState(normalizedContractId, "", options);
    const values = Array.isArray(state?.values) ? state.values : [];
    let unlabeledStateOwner = "";

    for (const entry of values) {
      const rawKey = decodeRpcStateBytes(entry?.key);
      const rawValue = decodeRpcStateBytes(entry?.value);
      const keyText = decodeBytesToUtf8(rawKey);
      const normalizedKeyText = keyText.trim();
      const valueText = decodeBytesToUtf8(rawValue);

      // NEAR contracts often store the root state under a dedicated STATE key.
      // Prefer that record instead of grabbing the first account-like string
      // from unrelated metadata entries such as version tags.
      if (normalizedKeyText === "STATE") {
        const decodedOwner = decodeContractOwnerIdFromStateBytesRaw(rawValue);
        if (decodedOwner) return decodedOwner;
      }

      if (!normalizedKeyText && !unlabeledStateOwner) {
        unlabeledStateOwner = decodeContractOwnerIdFromStateBytesRaw(rawValue);
      }

      if (/owner/i.test(normalizedKeyText)) {
        const fromValue = findOwnerAccountIdInText(valueText);
        if (fromValue) return fromValue;
      }

      const combined = `${keyText}\n${valueText}`;
      if (/owner/i.test(combined)) {
        const fromCombined = findOwnerAccountIdInText(combined);
        if (fromCombined) return fromCombined;
      }
    }

    if (unlabeledStateOwner) {
      return unlabeledStateOwner;
    }

    if (values.length === 1) {
      const rawValue = decodeRpcStateBytes(values[0]?.value);
      const decodedOwner = decodeContractOwnerIdFromStateBytesRaw(rawValue);
      if (decodedOwner) return decodedOwner;
    }
  } catch (error) {
    if (error?.name === "AbortError") throw error;
  }

  try {
    const owner = await callContractView(normalizedContractId, "get_owner_id", {}, options);
    const extracted = extractOwnerId(owner);
    return isNearAccountId(extracted) ? extracted.toLowerCase() : extracted;
  } catch {
    return "";
  }
}

const exportedMethodsCache = new Map();

export async function fetchContractExportedMethods(contractId, options = {}) {
  const normalizedContractId = String(contractId || "").trim().toLowerCase();
  if (!normalizedContractId) return [];

  if (exportedMethodsCache.has(normalizedContractId)) {
    return exportedMethodsCache.get(normalizedContractId);
  }

  const request = (async () => {
    const codeResult = await rpcViewCode(normalizedContractId, options);
    const codeBytes = decodeBase64ToBytes(
      codeResult?.code_base64 || codeResult?.code || ""
    );

    if (!codeBytes.length) return [];
    if (
      typeof WebAssembly === "undefined" ||
      typeof WebAssembly.compile !== "function" ||
      typeof WebAssembly.Module?.exports !== "function"
    ) {
      return [];
    }

    const module = await WebAssembly.compile(codeBytes);
    return WebAssembly.Module.exports(module)
      .map((entry) => String(entry?.name || "").trim())
      .filter(Boolean);
  })().catch((error) => {
    if (error?.name === "AbortError") {
      exportedMethodsCache.delete(normalizedContractId);
      throw error;
    }
    console.warn("fetchContractExportedMethods error:", error);
    return [];
  });

  exportedMethodsCache.set(normalizedContractId, request);
  return request;
}

export async function contractExportsMethod(contractId, methodName, options = {}) {
  const normalizedMethodName = String(methodName || "").trim();
  if (!normalizedMethodName) return null;

  const exportedMethods = await fetchContractExportedMethods(contractId, options);
  if (!Array.isArray(exportedMethods) || exportedMethods.length === 0) {
    return null;
  }

  return exportedMethods.includes(normalizedMethodName);
}

export async function fetchFtMetadata(ftContractId, options = {}) {
  try {
    return await rpcViewCall(ftContractId, "ft_metadata", {}, options);
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    console.warn("fetchFtMetadata error:", error);
    return null;
  }
}

export async function fetchFtStorageBalance(ftContractId, accountId, options = {}) {
  try {
    return await rpcViewCall(
      ftContractId,
      "storage_balance_of",
      { account_id: accountId },
      options
    );
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    console.warn("fetchFtStorageBalance error:", error);
    return null;
  }
}

export async function fetchFtStorageBalanceBounds(ftContractId, options = {}) {
  try {
    return await rpcViewCall(ftContractId, "storage_balance_bounds", {}, options);
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    console.warn("fetchFtStorageBalanceBounds error:", error);
    return null;
  }
}

export async function fetchNftListPage(
  nftContractId,
  accountId,
  { fromIndex = 0, limit = DEFAULT_PAGE_SIZE, signal } = {}
) {
  const page = await rpcViewCall(
    nftContractId,
    "nft_tokens_for_owner",
    {
      account_id: accountId,
      from_index: String(fromIndex),
      limit,
    },
    { signal }
  );

  if (!Array.isArray(page)) return [];
  return page.map((item) => withContractId(item, nftContractId));
}

export async function fetchAllNftList(contractIdOrAccountId, maybeAccountId, options = {}) {
  const accountId = maybeAccountId ?? contractIdOrAccountId;
  const nftContractId = maybeAccountId ? contractIdOrAccountId : FALLBACK_NFT_CONTRACT_ID;
  const pageSize = Math.max(1, Number(options?.limit || DEFAULT_PAGE_SIZE));

  let fromIndex = 0;
  let hasMore = true;
  const all = [];

  while (hasMore) {
    const page = await fetchNftListPage(nftContractId, accountId, {
      fromIndex,
      limit: pageSize,
      signal: options?.signal,
    });

    if (!page.length) break;

    all.push(...page);
    fromIndex += page.length;
    hasMore = page.length >= pageSize;
  }

  return all;
}

export async function fetchNftMetadata(nftContractId, options = {}) {
  try {
    return await rpcViewCall(nftContractId, "nft_metadata", {}, options);
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    console.warn("fetchNftMetadata error:", error);
    return null;
  }
}

export async function fetchNftToken(nftContractId, tokenId, options = {}) {
  const suppressErrorLog = options?.suppressErrorLog === true;
  const throwOnRateLimit = options?.throwOnRateLimit === true;
  const throwOnError = options?.throwOnError === true;
  try {
    const token = await rpcViewCall(
      nftContractId,
      "nft_token",
      { token_id: tokenId },
      options
    );
    return withContractId(token, nftContractId);
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    if (!suppressErrorLog) {
      console.warn("fetchNftToken error:", error);
    }
    if (throwOnRateLimit && error?.rpcRateLimited) throw error;
    if (throwOnError) throw error;
    return null;
  }
}

export async function fetchNftOwnerPreviewToken(nftContractId, accountId, options = {}) {
  try {
    const page = await fetchNftListPage(nftContractId, accountId, {
      fromIndex: 0,
      limit: 1,
      signal: options?.signal,
    });
    return page[0] || null;
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    console.warn("fetchNftOwnerPreviewToken error:", error);
    return null;
  }
}

export async function fetchNftOwnerSupply(nftContractId, accountId, options = {}) {
  try {
    const value = await rpcViewCall(
      nftContractId,
      "nft_supply_for_owner",
      { account_id: accountId },
      options
    );
    const count = Number(value ?? 0);
    return Number.isFinite(count) && count >= 0 ? count : 0;
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    try {
      const list = await fetchAllNftList(nftContractId, accountId, options);
      return Array.isArray(list) ? list.length : 0;
    } catch (fallbackError) {
      if (fallbackError?.name === "AbortError") throw fallbackError;
      console.warn("fetchNftOwnerSupply error:", error);
      return 0;
    }
  }
}

export async function fetchRabbitRarityBuckets(nftContractId, tokenIds, options = {}) {
  const normalizedContractId = String(nftContractId || "").trim().toLowerCase();
  const ids = Array.isArray(tokenIds)
    ? tokenIds.map((value) => String(value || "").trim()).filter(Boolean)
    : [];

  if (!ids.length) return {};

  if (normalizedContractId === "rabbits.nfts.tg") {
    try {
      const CHUNK_SIZE = 500;
      const chunks = [];
      for (let index = 0; index < ids.length; index += CHUNK_SIZE) {
        chunks.push(ids.slice(index, index + CHUNK_SIZE));
      }

      const payloads = await Promise.all(
        chunks.map(async (chunk) => {
          const response = await fetch(apiUrl("rabbits-rarity"), {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              contract_id: normalizedContractId,
              token_ids: chunk,
            }),
            signal: options?.signal,
          });

          if (!response.ok) {
            throw new Error(`Rabbit rarity API error ${response.status}`);
          }

          return {
            chunk,
            payload: await response.json(),
          };
        })
      );

      const items = {};
      payloads.forEach(({ chunk, payload }) => {
        const rawItems = payload?.items && typeof payload.items === "object" ? payload.items : {};
        chunk.forEach((tokenId) => {
          const rarity = normalizeRarityBucketValue(rawItems[tokenId]);
          if (rarity) {
            items[tokenId] = rarity;
          }
        });
      });
      return items;
    } catch (error) {
      if (error?.name === "AbortError") throw error;
      console.warn("fetchRabbitRarityBuckets API fallback:", error);
    }
  }

  const entries = await Promise.all(
    ids.map(async (tokenId) => {
      const token = await fetchNftToken(normalizedContractId || nftContractId, tokenId, options).catch(() => null);
      if (!token?.metadata) return [tokenId, ""];
      return [tokenId, deriveRarityBucketFromMetadata(token.metadata)];
    })
  );

  return Object.fromEntries(entries.filter(([tokenId]) => tokenId));
}

export async function fetchFinalBlockHeight(options = {}) {
  try {
    const result = await rpcRequest(
      {
        method: "block",
        params: { finality: "final" },
      },
      options
    );
    return Number(result?.header?.height || 0) || 0;
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    try {
      const fallback = await rpcRequest({ method: "status", params: [] }, options);
      return Number(fallback?.sync_info?.latest_block_height || 0) || 0;
    } catch {
      return 0;
    }
  }
}

export async function fetchFastNearNftContracts(accountId, options = {}) {
  if (!accountId) return [];

  const [indexedContracts, candidateHits] = await Promise.all([
    fetchFastNearAccountNftContracts(accountId, options).catch((error) => {
      if (error?.name === "AbortError") throw error;
      return [];
    }),
    Promise.all(
      getDiscoveryCandidates().map(async (candidate) => {
        const count = await fetchNftOwnerSupply(
          candidate.contractId,
          accountId,
          options
        ).catch(() => 0);

        if (Number(count) > 0) {
          return {
            contractId: candidate.contractId,
            name: candidate.name || candidate.contractId,
          };
        }

        const preview = await fetchNftOwnerPreviewToken(
          candidate.contractId,
          accountId,
          options
        ).catch(() => null);

        if (!preview) return null;
        return {
          contractId: candidate.contractId,
          name: candidate.name || candidate.contractId,
        };
      })
    ),
  ]);

  const merged = mergeDiscoveredNftContracts(
    indexedContracts,
    candidateHits.filter(Boolean)
  );

  return merged.sort((left, right) => {
    const byHeight =
      Number(right.lastUpdateBlockHeight || 0) - Number(left.lastUpdateBlockHeight || 0);
    if (byHeight !== 0) return byHeight;
    return String(left.name || left.contractId).localeCompare(
      String(right.name || right.contractId)
    );
  });
}
